// /frontend/src/api/adminAuthService.js
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { getApiBase } from "./apiUtils";

const API_BASE = getApiBase(); // e.g. http://127.0.0.1:5000/api
if (process.env.NODE_ENV !== "production") {
  console.log("🌐 adminAuthService API_BASE =", API_BASE);
}

// ---------- Token Keys ----------
const AUTH_TOKEN_KEY = "adminAuthToken";
const REFRESH_TOKEN_KEY = "adminRefreshToken";

// ---------- State ----------
let isRefreshing = false;
let isLoggingOut = false;
let refreshSubscribers = [];
let retryCount = 0;
const MAX_RETRY_COUNT = 3;

// ---------- Helpers ----------
const backendOrigin = (() => {
  try {
    const base = new URL(
      API_BASE,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );
    return base.origin;
  } catch {
    return typeof window !== "undefined" ? window.location.origin : "";
  }
})();

function resolveAbsoluteUrl(urlLike) {
  try {
    const base = new URL(
      API_BASE,
      typeof window !== "undefined" ? window.location.origin : "http://localhost"
    );
    return new URL(urlLike, base).href;
  } catch {
    return "";
  }
}

function isSameBackend(urlLike) {
  const abs = resolveAbsoluteUrl(urlLike);
  if (!abs || !backendOrigin) return false;
  try {
    return new URL(abs).origin === backendOrigin;
  } catch {
    return false;
  }
}

const joinApi = (p) => {
  let u = p || "/";
  if (!u.startsWith("http") && !u.startsWith("/")) u = "/" + u;
  if (u.startsWith("/api/")) u = u.replace(/^\/api(\/|$)/, "/");
  return `${API_BASE}${u}`;
};

// ---------- Token helpers ----------
export const getAuthToken = () => {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const getRefreshToken = () => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
};
export const saveAuthToken = (t) => {
  if (t)
    try {
      localStorage.setItem(AUTH_TOKEN_KEY, t);
    } catch {}
};
export const saveRefreshToken = (t) => {
  if (t)
    try {
      localStorage.setItem(REFRESH_TOKEN_KEY, t);
    } catch {}
};
export const saveAdminAuthToken = (at, rt) => {
  if (at) saveAuthToken(at);
  if (rt) saveRefreshToken(rt);
};
export const saveAdminRefreshToken = (rt) => {
  if (rt) saveRefreshToken(rt);
};

// ---------- Expiry checker ----------
export const isTokenExpired = (token) => {
  if (!token) return true;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp < Date.now() / 1000;
  } catch {
    return true;
  }
};

// ---------- Clear session ----------
export const clearAdminSession = () => {
  if (isLoggingOut) return;
  isLoggingOut = true;
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } finally {
    setTimeout(() => {
      if (typeof window !== "undefined") {
        const target = "/#/admin/login";
        if (!window.location.href.endsWith(target))
          window.location.href = target;
      }
      isLoggingOut = false;
    }, 200);
  }
};

// ---------- Refresh queue ----------
const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const notifyRefreshed = (payload) => {
  try {
    refreshSubscribers.forEach((cb) => {
      try {
        cb(payload);
      } catch {}
    });
  } finally {
    refreshSubscribers = [];
  }
};

// ---------- Refresh token (POST /api/admins/refresh, header-only) ----------
export const refreshAccessToken = async () => {
  // กันเรียกซ้ำพร้อมกัน
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      subscribeTokenRefresh(({ token, error }) =>
        error ? reject(error) : resolve(token || null)
      );
    });
  }

  isRefreshing = true;
  try {
    const rt = getRefreshToken();
    if (!rt) {
      console.warn("⚠️ No admin refresh token in storage. Skip refresh.");
      notifyRefreshed({ error: new Error("No refresh token") });
      return null;
    }

    const headers = { Authorization: `Bearer ${rt}` };

    // มาตรฐาน backend เรา: POST /api/admins/refresh (ไม่มี body)
    let data;
    try {
      const res = await axios.post(joinApi("/admins/refresh"), undefined, {
        withCredentials: false,
        headers,
      }); // header-only
      data = res?.data;
    } catch (e) {
      // เผื่อ backend เก่าเป็น GET
      if (e?.response?.status === 405) {
        const res = await axios.get(joinApi("/admins/refresh"), {
          withCredentials: false,
          headers,
        });
        data = res?.data;
      } else {
        throw e;
      }
    }

    const newAccess = data?.access_token || data?.access || data?.token?.access;
    if (!newAccess) {
      notifyRefreshed({
        error: new Error("No access token in refresh response"),
      });
      return null;
    }

    saveAuthToken(newAccess);
    retryCount = 0;
    notifyRefreshed({ token: newAccess });
    return newAccess;
  } catch (err) {
    console.error("❌ [admin refreshAccessToken]", err?.message || err);
    notifyRefreshed({ error: err || new Error("refresh failed") });
    return null;
  } finally {
    isRefreshing = false;
  }
};

// ---------- Axios Instance (header-only auth) ----------
const adminApiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: false, // สำคัญ: ไม่ใช้ cookies
});

adminApiClient.interceptors.request.use(
  async (config) => {
    // ปรับ URL ให้สัมบูรณ์ภายใต้ API_BASE
    if (config.url && !config.url.startsWith("http") && !config.url.startsWith("/")) {
      config.url = "/" + config.url;
    }
    if (config.url && config.url.startsWith("/api/")) {
      config.url = config.url.replace(/^\/api(\/|$)/, "/");
    }

    const sameBackend =
      (config.url && !config.url.startsWith("http")) ||
      isSameBackend(config.url);
    if (sameBackend) {
      let token = getAuthToken();

      if (process.env.NODE_ENV !== "production") {
        console.debug(
          "[adminApiClient][req] url=",
          config.url,
          "hasToken=",
          Boolean(token)
        );
      }

      if (!token) {
        if (retryCount >= MAX_RETRY_COUNT) {
          clearAdminSession();
          return Promise.reject(new Error("Too many refresh attempts"));
        }
        retryCount += 1;
        try {
          token = await refreshAccessToken();
        } catch {}
        if (!token) return Promise.reject(new Error("No valid admin token"));
      }

      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    // จัดการ Content-Type: ถ้าเป็น FormData ให้ browser ตั้งเอง
    const isForm =
      typeof FormData !== "undefined" &&
      (config.data instanceof FormData ||
        (config.data && config.data._isFormData));
    if (isForm) {
      delete config.headers?.["Content-Type"];
      delete config.headers?.["content-type"];
    } else if (config.data !== undefined) {
      config.headers = config.headers || {};
      if (!config.headers["Content-Type"] && !config.headers["content-type"]) {
        config.headers["Content-Type"] = "application/json";
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

adminApiClient.interceptors.response.use(
  (res) => {
    retryCount = 0;
    return res;
  },
  async (error) => {
    const original = error?.config || {};
    const status = error?.response?.status;

    if (process.env.NODE_ENV !== "production") {
      console.debug(
        "[adminApiClient][res] status=",
        status,
        "url=",
        original?.url,
        "retry=",
        original?._retry
      );
    }

    // จัดการ 401: ลอง refresh แล้ว retry 1 ครั้ง (รวมกับ MAX_RETRY_COUNT)
    if (status === 401 && !original._retry && isSameBackend(original.url)) {
      original._retry = true;

      if (retryCount >= MAX_RETRY_COUNT) {
        clearAdminSession();
        return Promise.reject(new Error("Token refresh failed"));
      }
      retryCount += 1;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh(({ token, error: err }) => {
            if (err || !token) {
              clearAdminSession();
              return reject(err || new Error("No token after refresh"));
            }
            original.headers = original.headers || {};
            original.headers.Authorization = `Bearer ${token}`;
            resolve(adminApiClient(original));
          });
        });
      }

      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return adminApiClient(original);
      }

      clearAdminSession();
    }

    return Promise.reject(error);
  }
);

// ---------- Admin APIs ----------
export const adminLogin = async (email, password) => {
  // สำคัญ: ไม่ใช้ cookies
  const { data } = await axios.post(
    joinApi("/admins/login"),
    { email, password },
    { withCredentials: false, headers: { "Content-Type": "application/json" } }
  );

  const at = data?.access_token || data?.access || data?.token?.access;
  const rt =
    data?.refresh_token || data?.refresh || data?.token?.refresh || null;

  if (!at) throw new Error("Invalid Login Response: Missing access token.");

  let adminInfo = data?.admin || null;
  try {
    const decoded = jwtDecode(at);
    const sub = decoded?.sub;
    let tokenRole = decoded?.role;
    let tokenAdminId = decoded?.admin_id;

    if (typeof sub === "object") {
      tokenRole = sub?.role || tokenRole;
      tokenAdminId = sub?.admin_id || tokenAdminId;
    } else if (typeof sub === "string") {
      tokenAdminId = sub;
    }

    if (!adminInfo) {
      adminInfo = {
        admin_id: tokenAdminId ?? null,
        email,
        role: tokenRole ?? null,
      };
    } else if (!adminInfo.role && tokenRole) {
      adminInfo.role = tokenRole;
    }
  } catch {
    adminInfo = adminInfo || { admin_id: null, email, role: null };
  }
  if (!adminInfo.role) adminInfo.role = "admin";

  saveAdminAuthToken(at, rt);
  return { access_token: at, refresh_token: rt, admin: adminInfo };
};

export const adminLogout = () => clearAdminSession();

export const fetchAuthenticatedAdmin = async () => {
  const { data } = await adminApiClient.get("/admins/me"); // header-only
  return data; // { admin_id, email, role, name }
};

// ---------- New: Admin Dashboard list APIs ----------
export const fetchAdminUsers = async (params = {}) => {
  // GET /api/admins/users → { users: [...] }
  const { data } = await adminApiClient.get("/admins/users", { params });
  return data; // ใช้ data.users ในหน้า Dashboard
};

export const fetchAdminSales = async () => {
  // GET /api/admins/sales → { sales: [...] }
  const { data } = await adminApiClient.get("/admins/sales");
  return data; // ใช้ data.sales ในหน้า Dashboard
};

export default adminApiClient;
